/**
 * The one notice every env-var save path shows after `PUT /projects/:id/env`
 * (and the matching DELETE): saved for prod, live, next start — or saved but
 * the asleep app could not be woken to receive the values (the owner's
 * running-project limit), said with the way out instead of a bare "apply when
 * your app next starts".
 *
 * @module
 */

import { emitNotification } from '../notifications.js'
import { sandboxErrorAction, sandboxErrorCopy } from './sandbox-error-copy.js'

/** The body `PUT`/`DELETE /projects/:id/env` answers with. */
export interface EnvSaveResponse {
  ok: boolean
  applied?: boolean
  /** Set when the asleep app could not be woken to receive the values (the owner's running-project limit). */
  refusal?: { error?: string; errorKey?: string }
}

type Translate = (
  key: string,
  values?: Record<string, string | number | boolean | Date>,
  options?: { defaultValue?: string },
) => string

/**
 * Show the refusal notice when the route reported one. Returns true when it
 * did (the caller then skips its own success notice).
 *
 * @param t - The translator.
 * @param data - The route's response body.
 * @returns True when a refusal was shown.
 */
export function notifyEnvSaveRefusal(t: Translate, data: EnvSaveResponse | undefined): boolean {
  const refusalKey = data?.refusal?.errorKey ?? null
  const refusalCopy = sandboxErrorCopy(refusalKey) ?? data?.refusal?.error ?? null
  if (!refusalCopy) return false
  emitNotification(
    t(
      'workspace.setup.savedButRefused',
      { reason: refusalCopy },
      { defaultValue: 'Saved, but not applied yet. {{reason}}' },
    ),
    'warning',
    sandboxErrorAction(refusalKey) ?? undefined,
  )
  return true
}

/**
 * The save-outcome notice: saved for prod, live, next start, or the refusal.
 *
 * @param t - The translator.
 * @param scope - Which environment was saved (`prod` has no live apply).
 * @param data - The route's response body.
 */
export function notifyEnvSaveOutcome(
  t: Translate,
  scope: string,
  data: EnvSaveResponse | undefined,
): void {
  if (scope !== 'prod' && notifyEnvSaveRefusal(t, data)) return
  emitNotification(
    scope === 'prod'
      ? t('workspace.setup.savedProd', undefined, {
          defaultValue: 'Saved — the keys apply on your next production deploy.',
        })
      : data?.applied
        ? t('workspace.setup.savedLive', undefined, {
            defaultValue: 'Saved — the keys are live in your app.',
          })
        : t('workspace.setup.savedNextStart', undefined, {
            defaultValue: 'Saved — the keys apply when your app next starts.',
          }),
    'info',
  )
}
